/** เส้นทางการผลิตจริง — ค้นด้วยรหัสป้าย Kanban หรือเลข lot แล้วเทียบแผน (routing) กับที่ทำจริงต่อ operation (อ่านอย่างเดียว) */

import { useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { stockFailureText } from '../../lib/inventory/stock';
import { fetchRouteTrace, type RouteTrace, type RouteTraceLinkage } from '../../lib/routeTraces/client';

const LINKAGE_TEXT: Readonly<Record<RouteTraceLinkage, string>> = {
  LOT: 'ตาม Lot',
  ORDER: 'ทั้งใบสั่งผลิต (ไม่มีการเชื่อม Lot)',
};

export interface RouteTraceScreenProps {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

const bangkok = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function bangkokTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const p: Record<string, string> = {};
  for (const part of bangkok.formatToParts(date)) {
    p[part.type] = part.value;
  }
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

type Mode = 'tag_code' | 'lot_number';

export function RouteTraceScreen({ apiBaseUrl, fetchImpl }: RouteTraceScreenProps) {
  const [mode, setMode] = useState<Mode>('tag_code');
  const [text, setText] = useState('');
  const [trace, setTrace] = useState<RouteTrace | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const search = async (event: FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (value === '') {
      setMessage('กรอกรหัสป้ายหรือเลข lot ที่จะค้น');
      return;
    }
    setLoading(true);
    setMessage(null);
    setTrace(null);
    const result = await fetchRouteTrace(mode === 'tag_code' ? { tag_code: value } : { lot_number: value }, {
      ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
      ...(fetchImpl === undefined ? {} : { fetchImpl }),
    });
    setLoading(false);
    if (result.status === 'ready') {
      setTrace(result.trace);
    } else if (result.status === 'not-found') {
      setMessage('ไม่พบป้ายหรือ lot นี้');
    } else {
      setMessage(stockFailureText(result));
    }
  };

  return (
    <div className="cap-page" data-testid="route-trace">
      <div className="cap-page__head">
        <h1 className="cap-page__title">เส้นทางการผลิตจริง</h1>
      </div>
      <form className="cap-card cap-card--form cap-form" onSubmit={(e) => void search(e)}>
        <div className="cap-form__grid">
          <label className="cap-field">
            <span className="cap-field__label">ค้นด้วย</span>
            <select
              className="cap-input"
              aria-label="ค้นด้วย"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              <option value="tag_code">รหัสป้าย Kanban</option>
              <option value="lot_number">เลข lot</option>
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสป้าย / เลข lot</span>
            <input
              className="cap-input"
              aria-label="รหัสป้าย / เลข lot"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
        </div>
        <div className="cap-form__actions cap-form__actions--padded">
          <button type="submit" className="cap-button cap-button--primary" disabled={loading}>
            ค้นหา
          </button>
        </div>
      </form>

      {message !== null && (
        <p className="cap-notice__note" role="alert" data-testid="route-trace-message">
          {message}
        </p>
      )}

      {trace !== null && <TraceView trace={trace} />}
    </div>
  );
}

function TraceView({ trace }: { readonly trace: RouteTrace }) {
  const missing = new Set(trace.missing_operations);
  return (
    <section className="cap-card" data-testid="route-trace-result">
      <h2 className="cap-page__title">
        {trace.order_no} · {trace.product_code} {trace.product_name}
      </h2>
      <p>
        {trace.complete ? (
          <StatusBadge tone="success">ครบทุกขั้นตอน</StatusBadge>
        ) : (
          <span data-testid="route-trace-missing">
            <StatusBadge tone="danger">ขาดขั้นตอน</StatusBadge> ขาด operation: {trace.missing_operations.join(', ')}
          </span>
        )}
      </p>
      <p data-testid="route-trace-basis">
        {trace.trace_basis === 'LOT' ? (
          <StatusBadge tone="success">ติดตามตาม Lot ทุกขั้น</StatusBadge>
        ) : (
          <>
            <StatusBadge tone="warning">บางขั้นไม่มีการเชื่อม Lot</StatusBadge> ขั้นนั้นแสดงทุกรอบของใบสั่งผลิต
          </>
        )}
      </p>
      <div className="cap-route-trace__grid" role="table" aria-label="แผนเทียบจริง">
        <strong role="columnheader">แผน (Routing)</strong>
        <strong role="columnheader">ที่ทำจริง</strong>
        {trace.planned_route.map((step) => {
          const actual = trace.actual.find((a) => a.operation_seq === step.operation_seq);
          const isMissing = missing.has(step.operation_seq);
          return (
            <div key={step.operation_seq} style={{ display: 'contents' }} data-testid={`route-trace-step-${step.operation_seq}`}>
              <div role="cell">
                <strong>{step.operation_seq}</strong> · {step.work_center_code} {step.work_center_name}
              </div>
              <div role="cell">
                {isMissing && <StatusBadge tone="danger">ยังไม่ได้ทำ</StatusBadge>}
                {actual !== undefined && (
                  <div data-testid="route-trace-linkage">{LINKAGE_TEXT[actual.linkage]}</div>
                )}
                {actual?.confirmations.map((c) => (
                  <div key={c.confirmation_id} className="cap-route-trace__cycle" data-testid="route-trace-cycle">
                    <div>
                      {actual.work_center_code} · {bangkokTime(c.started_at)} → {bangkokTime(c.ended_at)}
                      {' · '}
                      <span data-testid="route-trace-operator">{c.started_by_employee_id ?? '—'}</span>
                    </div>
                    <div>
                      ดี {c.qty_good} · เสีย {c.qty_scrap} · แก้ {c.qty_rework} · {c.status}
                    </div>
                    <div data-testid="route-trace-machines">
                      เครื่อง:{' '}
                      {c.machines.length === 0
                        ? '—'
                        : c.machines.map((m) => `${m.machine_code} ${m.name}`.trim()).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
